import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Landmark, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useGallery } from "../AttachmentStrip";
import HoverPreview from "../HoverPreview";

export const ibanOk = (v) => {
  const s = (v || "").replace(/\s/g, "").toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(s) || (s.startsWith("FR") && s.length !== 27)) return false;
  const num = (s.slice(4) + s.slice(0, 4)).replace(/[A-Z]/g, (c) => String(c.charCodeAt(0) - 55));
  return num.match(/\d{1,7}/g).reduce((r, part) => Number(String(r) + part) % 97, 0) === 1;
};

function Rib({ id }) {
  const [items] = useGallery(id, "rib");
  const rib = (items || []).find((f) => /\brib\b|relev[ée] d.identit/i.test(`${f.label} ${f.filename}`));
  if (items === null) return <Loader2 className="w-3.5 h-3.5 animate-spin" />;
  if (!rib) return <span className="text-norea-textMuted italic">pas de RIB</span>;
  return (
    <HoverPreview dossierId={id} file={rib} className="inline-flex" testid={`iban-rib-preview-${id}`}>
      <Link to={`/dossiers/${id}`} className="text-norea-primary hover:underline inline-flex items-center gap-1" data-testid={`iban-rib-${id}`}><Landmark className="w-3.5 h-3.5" />RIB</Link>
    </HoverPreview>
  );
}

function Row({ it, onDone }) {
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = ibanOk(v);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/checks/iban/${it.id}`, { iban: v }); toast.success(`IBAN de ${it.client} corrigé`); onDone(it.id); }
    catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); setBusy(false); }
  };
  return (
    <tr className="border-t border-norea-border" data-testid={`iban-row-${it.id}`}>
      <td className="py-2 pr-3"><Link to={`/dossiers/${it.id}`} className="font-medium text-norea-primary hover:underline">{it.client}</Link><div className="text-[11px] text-norea-textMuted">{[it.ville, it.produit].filter(Boolean).join(" · ")}</div></td>
      <td className="pr-3 font-mono text-xs text-red-700" data-testid={`iban-current-${it.id}`}>{it.iban}</td>
      <td className="pr-3 text-xs"><Rib id={it.id} /></td>
      <td className="pr-2">
        <div className="relative">
          <input value={v} onChange={(e) => setV(e.target.value)} placeholder="FR76 …" className={`input-field !py-1 font-mono text-xs pr-7 ${v && !ok ? "!border-red-400" : ""} ${ok ? "!border-emerald-500" : ""}`} data-testid={`iban-input-${it.id}`} />
          {v && (ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600 absolute right-2 top-1.5" data-testid={`iban-ok-${it.id}`} /> : <XCircle className="w-4 h-4 text-red-500 absolute right-2 top-1.5" data-testid={`iban-bad-${it.id}`} />)}
        </div>
        {v && !ok && <div className="text-[10px] text-red-600 mt-0.5">Clé de contrôle invalide</div>}
      </td>
      <td><button onClick={save} disabled={!ok || busy} className="btn-primary !py-1 text-xs disabled:opacity-40" data-testid={`iban-save-${it.id}`}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Enregistrer"}</button></td>
    </tr>
  );
}

export default function IbanCheckCard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { if (isAdmin) api.get("/checks/iban").then((r) => setItems(r.data.items)).catch(() => {}); }, [isAdmin]);
  if (!isAdmin || !items.length) return null;
  const shown = open ? items : items.slice(0, 8);
  return (
    <div className="card-flat p-5 mb-6" data-testid="iban-check-card">
      <h3 className="font-heading text-lg font-medium text-norea-text">IBAN à vérifier <span className="text-sm text-norea-textMuted" data-testid="iban-check-count">({items.length})</span></h3>
      <p className="text-xs text-norea-textMuted mt-1 mb-3">IBAN dont la clé de contrôle est fausse (chiffre manquant ou mal lu). Survolez « RIB » pour le consulter, saisissez l'IBAN corrigé : la clé est vérifiée immédiatement et la ligne disparaît une fois enregistrée.</p>
      <table className="w-full text-sm">
        <thead className="text-[11px] text-norea-textMuted text-left"><tr><th className="pb-1">Client</th><th>IBAN actuel</th><th>RIB</th><th className="w-80">IBAN corrigé</th><th></th></tr></thead>
        <tbody>{shown.map((it) => <Row key={it.id} it={it} onDone={(id) => setItems((x) => x.filter((y) => y.id !== id))} />)}</tbody>
      </table>
      {items.length > 8 && <button onClick={() => setOpen(!open)} className="btn-ghost text-xs mt-2" data-testid="iban-check-toggle">{open ? "Réduire" : `Voir les ${items.length}`}</button>}
    </div>
  );
}
